/**
 * Precision Storytelling — canonical module list (single source of truth).
 *
 * Slugs here MUST match, exactly and everywhere:
 *   - the App.jsx route paths        (/skills/precision-storytelling/<slug>)
 *   - the backend `moduleId` values  (routes/storytelling.js switch)
 *   - the data-file module keys      (storytellingExercises.js)
 * This is deliberately imported everywhere rather than re-typed per file —
 * Copywriting's landing used `the-living-ad` while its routes used `living-ad`,
 * which 404'd 4 of 5 module cards. Don't repeat that: import from here.
 *
 * `lessonId` values are the REAL primary keys assigned by Supabase when the
 * lessons rows were seeded (lessons 4–8; AI Prompting owns 1–3). There is no
 * `skill_id` column in `lessons`/`user_progress` — a skill is scoped purely by
 * its non-overlapping lesson_id range, so progress reads/writes key off these.
 */

export const SKILL_ID   = 'precision-storytelling';
export const SKILL_ROUTE = '/skills/precision-storytelling';

// Per-module character illustration slots, day + night variants. Files live at
// frontend/public/precision-storytelling/<slug>-{day,night}.png and are swapped
// by CSS only (the `.dark` ancestor toggles which <img> is visible — no JS).
// Filenames match the slugs, so dropping finished art in is a pure asset swap.
//
// `hasArt: true` means the PNG (which already contains the cloud + character +
// accessory) exists and should FILL the tile as its actual visual — the tile
// then draws no code-SVG cloud behind it (that was the double-cloud bug). A
// module without `hasArt` would keep the plain SVG empty-cloud placeholder
// until its art lands, at which point flipping this flag is the only change
// needed — all five now have real art, so this is a dormant fallback path,
// not dead code.
// (Concepts: shears-hedgehog, weathervane-fox, dot-connecting owl,
// archer-rabbit, boomerang-otter.)
// `characterName` is the character's given name, revealed letter-by-letter
// when the tile's number badge is clicked (see CloudTile in
// StorytellingLanding.jsx) — distinct from `characterAlt`, which stays the
// static, descriptive `<img alt>` text.
//
// `flipHorizontal` (Callback only): mirrors the character art via
// `scaleX(-1)` so Echo's boomerang trails toward the opposite side from
// Fletch's (Pitch Arc's) arrow — the two read as facing away from each
// other rather than in the same direction.
export const STORYTELLING_MODULES = [
  { slug: 'origin-cut',    title: 'The Origin Cut',    lessonId: 4, order: 1, color: '#db2777', hasArt: true, characterImageDay: '/precision-storytelling/origin-cut-day.png',    characterImageNight: '/precision-storytelling/origin-cut-night.png',    characterAlt: 'Shears-hedgehog', characterName: 'Bramble' },
  { slug: 'turning-point', title: 'The Turning Point', lessonId: 5, order: 2, color: '#e0559b', hasArt: true, characterImageDay: '/precision-storytelling/turning-point-day.png', characterImageNight: '/precision-storytelling/turning-point-night.png', characterAlt: 'Weathervane-fox', characterName: 'Pivot' },
  { slug: 'data-story',    title: 'The Data Story',    lessonId: 6, order: 3, color: '#c026d3', hasArt: true, characterImageDay: '/precision-storytelling/data-story-day.png',    characterImageNight: '/precision-storytelling/data-story-night.png',    characterAlt: 'Dot-connecting owl', characterName: 'Nova' },
  { slug: 'pitch-arc',     title: 'The Pitch Arc',     lessonId: 7, order: 4, color: '#9333ea', hasArt: true, characterImageDay: '/precision-storytelling/pitch-arc-day.png',     characterImageNight: '/precision-storytelling/pitch-arc-night.png',     characterAlt: 'Archer-rabbit', characterName: 'Fletch' },
  { slug: 'callback',      title: 'The Callback',      lessonId: 8, order: 5, color: '#7c3aed', hasArt: true, characterImageDay: '/precision-storytelling/callback-day.png',      characterImageNight: '/precision-storytelling/callback-night.png',      characterAlt: 'Boomerang-otter', characterName: 'Echo', flipHorizontal: true },
];

export const STORYTELLING_SLUGS      = STORYTELLING_MODULES.map((m) => m.slug);
export const STORYTELLING_LESSON_IDS = STORYTELLING_MODULES.map((m) => m.lessonId);

export const moduleForSlug   = (slug) => STORYTELLING_MODULES.find((m) => m.slug === slug) ?? null;
export const lessonIdForSlug = (slug) => moduleForSlug(slug)?.lessonId ?? null;
